import { z } from 'zod';
import { ESTADOS_CITA, MAX_DIAS_DISPONIBILIDAD } from '../catalogos/agenda.js';
import { esquemaId } from './comunes.js';
/**
 * Citas del consultorio.
 *
 * Hay dos formas de crear una: desde dentro, con sesion, y desde el portal
 * publico del consultorio. No comparten esquema a proposito. La de fuera no
 * puede elegir estado, ni referirse a un paciente por identificador, ni tocar
 * campos administrativos: si compartieran esquema, cada campo nuevo habria que
 * acordarse de cerrarlo en el lado publico, y tarde o temprano se olvida.
 */
/**
 * Instante de la cita, en UTC.
 *
 * Aqui SI es un instante, al contrario que la fecha de nacimiento —que se
 * guarda como texto para que no se corra un dia—. Una cita ocurre en un momento
 * concreto; lo que cambia es como se muestra, y eso lo decide la zona horaria
 * del consultorio.
 */
export const esquemaInstante = z.string().trim().datetime({
    offset: true,
    message: 'Use una fecha y hora ISO con zona (ej. 2026-09-01T16:00:00Z)',
});
const datosDeContacto = {
    nombreContacto: z.string().trim().max(120).default(''),
    telefono: z.string().trim().max(40).default(''),
    correo: z.union([z.literal(''), z.string().trim().toLowerCase().email()]).default(''),
};
/** Alta desde el consultorio. */
export const esquemaCrearCita = z
    .object({
    medicoId: esquemaId,
    inicio: esquemaInstante,
    /** Si no se dice, manda la duracion configurada del consultorio. */
    duracionMinutos: z.coerce.number().int().min(5).max(240).optional(),
    /** Una cita puede ser de alguien que todavia no es paciente del
     * expediente: una primera consulta se agenda antes de existir. */
    pacienteId: esquemaId.optional(),
    ...datosDeContacto,
    motivo: z.string().trim().max(300).default(''),
    notas: z.string().trim().max(1000).default(''),
})
    .refine((cita) => Boolean(cita.pacienteId) || cita.nombreContacto.length >= 2, {
    message: 'Indique el paciente del expediente o, si es nuevo, a nombre de quien va la cita',
    path: ['nombreContacto'],
});
/**
 * Solicitud desde el portal publico del consultorio.
 *
 * Superficie deliberadamente estrecha: sin `pacienteId` —el portal no puede
 * nombrar a un paciente del expediente, ni averiguar si existe—, sin estado,
 * sin notas administrativas y sin eleccion de duracion.
 */
export const esquemaSolicitarCita = z.object({
    medicoId: esquemaId.optional(),
    inicio: esquemaInstante,
    nombreContacto: z.string().trim().min(2, 'Escriba su nombre').max(120),
    telefono: z.string().trim().min(7, 'Escriba un teléfono de contacto').max(40),
    correo: z.union([z.literal(''), z.string().trim().toLowerCase().email()]).default(''),
    motivo: z.string().trim().max(300).default(''),
});
export const esquemaActualizarCita = z.object({
    inicio: esquemaInstante.optional(),
    duracionMinutos: z.coerce.number().int().min(5).max(240).optional(),
    medicoId: esquemaId.optional(),
    pacienteId: esquemaId.optional(),
    nombreContacto: z.string().trim().max(120).optional(),
    telefono: z.string().trim().max(40).optional(),
    correo: z.union([z.literal(''), z.string().trim().toLowerCase().email()]).optional(),
    motivo: z.string().trim().max(300).optional(),
    notas: z.string().trim().max(1000).optional(),
});
/**
 * Cambio de estado.
 *
 * Cancelar exige motivo. Una agenda sin el porque de las cancelaciones no
 * sirve para saber si el problema es el horario, el recordatorio o el precio.
 */
export const esquemaCambiarEstadoCita = z
    .object({
    estado: z.enum(ESTADOS_CITA),
    motivo: z.string().trim().max(300).default(''),
})
    .refine((cambio) => cambio.estado !== 'cancelada' || cambio.motivo.length >= 3, {
    message: 'Explique por qué se cancela la cita',
    path: ['motivo'],
});
export const esquemaListarCitas = z.object({
    /** Rango de fechas, como dia suelto: la agenda se consulta por dias, no por
     * instantes. El servicio los convierte a la zona del consultorio. */
    desde: z
        .string()
        .trim()
        .regex(/^\d{4}-\d{2}-\d{2}$/, 'Use el formato AAAA-MM-DD')
        .optional(),
    hasta: z
        .string()
        .trim()
        .regex(/^\d{4}-\d{2}-\d{2}$/, 'Use el formato AAAA-MM-DD')
        .optional(),
    medicoId: esquemaId.optional(),
    pacienteId: esquemaId.optional(),
    estado: z.enum(ESTADOS_CITA).optional(),
    pagina: z.coerce.number().int().min(1).default(1),
    porPagina: z.coerce.number().int().min(1).max(200).default(50),
});
/** Consulta de disponibilidad del portal publico. */
export const esquemaConsultarDisponibilidad = z.object({
    desde: z
        .string()
        .trim()
        .regex(/^\d{4}-\d{2}-\d{2}$/, 'Use el formato AAAA-MM-DD')
        .optional(),
    dias: z.coerce.number().int().min(1).max(MAX_DIAS_DISPONIBILIDAD).default(14),
    medicoId: esquemaId.optional(),
});
//# sourceMappingURL=cita.js.map